import type { NodeNeighbor, MeshCoreLinkEvidence } from '../../api/types';
import { MESHCORE_SOURCE_LABEL, MESHCORE_SNR_COLOR_HEX } from '../../lib/definitions/protocols';
import { navigate } from '../../lib/router';
import { relativeTime } from '../../lib/format';
import { EvidenceSummary } from './presentation';


// --- MeshCore neighbours section (2026-09-26 board item; merged into one
// row per neighbour per direction in the 2026-09-26 review follow-up) ---
// Real per-link SNR evidence (meshcore_links, migration 0036) replacing
// the old single "Signal (SNR)" tile. The API already merges every
// underlying source/resolution combination for a (neighbour, direction)
// pair into ONE MeshCoreLinkEvidence — this table renders exactly one row
// per entry it's handed, no further merging needed here. `sources` is
// always shown plainly ("our radio" vs "CoreScope, external", never merged
// silently); `evidence_summary` is the per-
// resolution breakdown as text (e.g. "direct 6 · relayed 3,541 · unique
// prefix 720") — what used to be separate, confusingly-duplicate-looking
// rows for the same neighbour. The SNR shown is the running MEAN, not
// best, since best is outlier-prone — best is still shown
// as a secondary value. `snr_color` comes pre-computed from the backend
// (audit/snr.py, per the stated policy), never re-derived here.
export interface MeshCoreLinkRow {
  neighbor: NodeNeighbor;
  link: MeshCoreLinkEvidence;
}


// MeshCore has no name-uniqueness enforcement — two entirely different
// physical nodes can and do advertise the same long_name. Always showing
// a short id suffix keeps every row honestly distinguishable regardless.
function shortNativeId(nativeId: string): string {
  return nativeId.slice(0, 8);
}


export function MeshCoreLinkTable({ title, rows, emptyReason }: { title: string; rows: MeshCoreLinkRow[]; emptyReason: string }) {
  if (rows.length === 0) {
    return (
      <div className="node-neighbors-meshcore-group">
        <h5>{title}</h5>
        <div className="empty-state">None recorded yet — {emptyReason}</div>
      </div>
    );
  }
  return (
    <div className="node-neighbors-meshcore-group">
      <h5>
        {title} ({rows.length})
      </h5>
      {/* 2026-09-26 operator-review fix 5: wrapped in a scrolling div (same
          convention as .telemetry-history-wrap below) — this table's
          columns, at nowrap width, can exceed the card's width on a node
          with long evidence text (e.g. a well-connected repeater's
          "relayed 3,541 · unique prefix 720"); without this the Source
          column silently overflowed past the card edge instead of
          scrolling into view. */}
      {/* Phone overflow fix (2026-09-27): at 390px this table's columns
          (Node, Evidence, Count, SNR, RSSI, Last heard, Source) ran off the
          right edge — the existing overflow-x:auto wrap only helped on
          screens wide enough to scroll comfortably. Below 640px this stacks
          each row as its own card instead, reusing BehaviourTab.tsx's Nodes
          list phone-reflow pattern (.behaviour-nodes-table's own
          data-label-driven card CSS, global.css) verbatim rather than
          inventing a second one — see that block's own comment. */}
      <div className="node-neighbors-meshcore-table-wrap meshcore-links-table-wrap">
        <table className="node-neighbors-meshcore-table meshcore-links-table">
          <thead>
            <tr>
              <th>Node</th>
              {/* Fix 3: header tooltip spelling out what each evidence kind
                  means and that the counts sum to the Count column, verbatim
                  per the review brief. */}
              <th title="How this neighbour was identified, with counts: direct = heard with no repeater in between; relayed = this node was the last repeater before the listener; via relays = heard through repeaters; the hop was matched to this node by its unique ID prefix; Resolved By Path = the short ID matched several repeaters and the rest of the path left only this one. The counts add up to Count.">
                Evidence
              </th>
              <th>Count</th>
              <th>SNR (avg / best)</th>
              <th>RSSI (avg / best)</th>
              <th>Last heard</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ neighbor, link }, i) => {
              const label = neighbor.long_name || neighbor.short_name || neighbor.native_id;
              // 2026-09-26 fix: 0 dBm RSSI is never a real LoRa reception
              // level — the backend (observers/meshcore_links.py,
              // _effective_snr_rssi) already folds an rssi==0 AND snr==0
              // observation to null everywhere (sums/samples/best/latest)
              // before it ever reaches this table, but it still counts
              // toward observation_count. When BOTH means come back null
              // there was never a real signal reading for this link at
              // all — show that honestly instead of a misleading "0.0 dB
              // best 0.0 dB" / "0 / 0" (RidgeWatcher/ACE_Post, the bug
              // this fix addresses), with no colour dot since there is no
              // SNR to classify.
              const noSignalReading = link.snr_mean == null && link.rssi_mean == null;
              const snrAvg = link.snr_mean != null ? `${link.snr_mean.toFixed(1)} dB` : "--";
              const snrBest = link.snr_best != null ? `${link.snr_best.toFixed(1)} dB` : "--";
              const rssiAvg = link.rssi_mean != null ? `${Math.round(link.rssi_mean)}` : "--";
              const rssiBest = link.rssi_best != null ? `${Math.round(link.rssi_best)}` : "--";
              // Fix 5: comma-joined (was " + "), matching how the review
              // brief phrased a two-source row ("our radio, CoreScope
              // (external)").
              const sources = link.sources.map((s) => MESHCORE_SOURCE_LABEL[s]).join(", ");
              return (
                <tr key={`${neighbor.native_id}-${i}`}>
                  <td
                    className="meshcore-links-name-cell"
                    onClick={() => navigate(`#/nodes/${encodeURIComponent(neighbor.native_id)}`)}
                    style={{ cursor: "pointer" }}
                  >
                    {label} <span className="node-neighbors-meshcore-shortid">{shortNativeId(neighbor.native_id)}</span>
                  </td>
                  <td data-label="Evidence"><EvidenceSummary text={link.evidence_summary} /></td>
                  <td data-label="Count">{link.observation_count}</td>
                  {noSignalReading ? (
                    <td colSpan={2} data-label="Signal" className="node-neighbors-meshcore-no-signal">
                      no signal reading
                    </td>
                  ) : (
                    <>
                      <td data-label="SNR (avg / best)">
                        {/* Fix 4: the coloured dot sits directly against the
                            AVERAGE (snr_color is itself computed from snr_mean,
                            audit/snr.py) — "best" trails after it, in grey, as
                            a secondary reading rather than sharing the dot. */}
                        {link.snr_color && (
                          <span
                            className="node-neighbors-meshcore-swatch"
                            style={{ background: MESHCORE_SNR_COLOR_HEX[link.snr_color] }}
                          />
                        )}
                        {snrAvg}
                        <span className="node-neighbors-meshcore-snr-best"> best {snrBest}</span>
                      </td>
                      <td data-label="RSSI (avg / best)">
                        {rssiAvg} / {rssiBest}
                      </td>
                    </>
                  )}
                  <td data-label="Last heard">{relativeTime(link.last_seen_at)}</td>
                  <td data-label="Source">{sources}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
